/**
 * Реестр прав. Источник правды для всей системы.
 *
 * Таблицы Permission / RolePermission в БД — зеркало этого файла, наполняемое
 * сидом. Расхождение ловится при старте API (`assertPermissionRegistryInSync`).
 */

import type { AccessLevel } from './types.js';

export const PERMISSIONS = [
  // ── Telegram-аккаунты ──
  'telegram.account.view',
  'telegram.account.identity.view',
  'telegram.account.connect',
  'telegram.account.disconnect',
  'telegram.account.update',
  'telegram.account.delete',

  /**
   * Управление узлами Telegram Service.
   *
   * Отдельное право, а не часть подключения аккаунтов: узел — это сервер, а не
   * учётная запись. Тот, кто заводит аккаунты, не обязан распоряжаться тем, на
   * каком железе они живут, и наоборот.
   */
  'telegram.node.manage',

  // ── Чаты как контейнеры ──
  'telegram.chat.view',
  'telegram.chat.delete',
  'telegram.chat.metadata.update',
  'telegram.chat.assign',

  // ── Папки ──
  'folder.manage',

  // ── Быстрые ответы ──
  'quickreply.view',
  'quickreply.manage',

  // ── Сообщения ──
  'telegram.message.view',
  'telegram.message.send',
  'telegram.message.reply',
  'telegram.message.edit',
  'telegram.message.delete',

  // ── Назначения ──
  'account.assign.senior',
  'account.assign.manager',
  'account.assign.intern',
  'account.unassign',

  // ── Пользователи ──
  'user.view',
  'user.view.all',
  'user.create',
  'user.update',
  'user.delete',
  'user.role.change',

  // ── Аудит ──
  'audit.view',
  'audit.view.scoped',

  // ── Браузерные политики ──
  'browser.policy.view',
  'browser.policy.manage',

  // ── Разделы рабочего места (CRM, WhatsApp, браузер) ──
  //
  // Право ОДНО и только на управление. Видимости раздела в правах нет
  // намеренно: она определяется поимённой выдачей, как и доступ к
  // Telegram-аккаунтам. Роль отвечает, что человек умеет; выдача — к чему
  // он допущен, и отзывается она одной строкой.
  'section.manage',

  // ── Апартаменты: Airbnb и Booking (docs/STAYS.md) ──
  //
  // Видимость самих аккаунтов — поимённая выдача (StayAccountAccess), как у
  // разделов. Права отвечают на «что человек умеет в модуле».
  'stays.view',
  'stays.book',
  'stays.property.moderate',
  'stays.booking.view.all',
  'stays.account.manage',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

const PERMISSION_SET: ReadonlySet<string> = new Set(PERMISSIONS);

export function isPermission(value: string): value is Permission {
  return PERMISSION_SET.has(value);
}

/**
 * Псевдонимы из §13 ТЗ.
 *
 * ТЗ перечисляет telegram.chat.send / .reply / .edit наряду с
 * telegram.message.*. Это одни и те же операции. Оба написания принимаются на
 * входе, решение всегда принимается по каноническому ключу.
 *
 * Причина нормализации, а не дублирования grants: два синонима с раздельными
 * наборами прав рано или поздно разъезжаются, и одно из них становится
 * забытой дырой.
 */
export const PERMISSION_ALIASES: Readonly<Record<string, Permission>> = {
  'telegram.chat.send': 'telegram.message.send',
  'telegram.chat.reply': 'telegram.message.reply',
  'telegram.chat.edit': 'telegram.message.edit',
};

export function normalizePermission(value: string): Permission | null {
  if (isPermission(value)) return value;
  return PERMISSION_ALIASES[value] ?? null;
}

export type PermissionScopeKind = 'global' | 'account';

export interface PermissionMeta {
  readonly category: string;
  readonly description: string;
  readonly scope: PermissionScopeKind;
  /** Требуемый эффективный уровень. null для глобальных прав. */
  readonly level: AccessLevel | null;
}

export const PERMISSION_META: Readonly<Record<Permission, PermissionMeta>> = {
  'telegram.account.view': {
    category: 'telegram.account',
    description: 'Видеть Telegram-аккаунт (маскированно или полно)',
    scope: 'account',
    level: 'READ_ONLY',
  },
  'telegram.account.identity.view': {
    category: 'telegram.account',
    description: 'Видеть displayName / username / phone / telegramUserId аккаунта',
    scope: 'account',
    level: 'READ_ONLY',
  },
  'telegram.account.connect': {
    category: 'telegram.account',
    description: 'Подключить новый Telegram-аккаунт',
    scope: 'global',
    level: null,
  },
  'telegram.node.manage': {
    category: 'telegram.account',
    description: 'Заводить и настраивать узлы Telegram Service',
    scope: 'global',
    level: null,
  },
  'telegram.account.disconnect': {
    category: 'telegram.account',
    description: 'Отключить (logout) Telegram-аккаунт',
    scope: 'global',
    level: null,
  },
  'telegram.account.update': {
    category: 'telegram.account',
    description: 'Изменить alias и настройки аккаунта',
    scope: 'account',
    level: 'MANAGE',
  },
  'telegram.account.delete': {
    category: 'telegram.account',
    description: 'Удалить Telegram-аккаунт из системы',
    scope: 'account',
    level: 'FULL',
  },

  'folder.manage': {
    category: 'folder',
    description: 'Создавать, переименовывать и удалять папки чатов',
    scope: 'global',
    level: null,
  },

  'quickreply.view': {
    category: 'quickreply',
    description: 'Видеть список быстрых ответов и вставлять их в сообщение',
    scope: 'global',
    level: null,
  },
  'quickreply.manage': {
    category: 'quickreply',
    description: 'Создавать, изменять и удалять быстрые ответы',
    scope: 'global',
    level: null,
  },

  'telegram.chat.view': {
    category: 'telegram.chat',
    description: 'Видеть чат и открывать его',
    scope: 'account',
    level: 'READ_ONLY',
  },
  'telegram.chat.delete': {
    category: 'telegram.chat',
    description: 'Удалить чат или историю',
    scope: 'account',
    level: 'FULL',
  },
  'telegram.chat.metadata.update': {
    category: 'telegram.chat',
    description: 'Менять внутренний статус, теги, заметки',
    scope: 'account',
    level: 'MANAGE',
  },
  'telegram.chat.assign': {
    category: 'telegram.chat',
    description: 'Назначить ответственного менеджера на чат',
    scope: 'account',
    level: 'MANAGE',
  },

  'telegram.message.view': {
    category: 'telegram.message',
    description: 'Читать сообщения и вложения',
    scope: 'account',
    level: 'READ_ONLY',
  },
  'telegram.message.send': {
    category: 'telegram.message',
    description: 'Отправлять сообщения',
    scope: 'account',
    level: 'MANAGE',
  },
  'telegram.message.reply': {
    category: 'telegram.message',
    description: 'Отвечать на сообщения',
    scope: 'account',
    level: 'MANAGE',
  },
  'telegram.message.edit': {
    category: 'telegram.message',
    description: 'Редактировать сообщения',
    scope: 'account',
    level: 'MANAGE',
  },
  'telegram.message.delete': {
    category: 'telegram.message',
    description: 'Удалять сообщения',
    scope: 'account',
    level: 'FULL',
  },

  'account.assign.senior': {
    category: 'assignment',
    description: 'Закрепить Telegram-аккаунт за Senior Manager',
    scope: 'global',
    level: null,
  },
  'account.assign.manager': {
    category: 'assignment',
    description: 'Выдать менеджеру доступ к аккаунту',
    scope: 'account',
    level: 'MANAGE',
  },
  'account.assign.intern': {
    category: 'assignment',
    description: 'Выдать стажёру read-only доступ к аккаунту',
    scope: 'account',
    level: 'MANAGE',
  },
  'account.unassign': {
    category: 'assignment',
    description: 'Отозвать доступ к аккаунту',
    scope: 'account',
    level: 'MANAGE',
  },

  'user.view': {
    category: 'user',
    description: 'Смотреть список пользователей (урезанная проекция)',
    scope: 'global',
    level: null,
  },
  'user.view.all': {
    category: 'user',
    // Отделено от user.view намеренно. Senior обязан видеть кандидатов на
    // назначение, но не весь штат с email'ами и датами входов — это раскрывает
    // структуру организации без необходимости для его работы.
    description: 'Смотреть полные карточки всех пользователей',
    scope: 'global',
    level: null,
  },
  'user.create': {
    category: 'user',
    description: 'Создавать пользователей',
    scope: 'global',
    level: null,
  },
  'user.update': {
    category: 'user',
    description: 'Изменять профиль пользователя',
    scope: 'global',
    level: null,
  },
  'user.delete': {
    category: 'user',
    description: 'Деактивировать пользователя',
    scope: 'global',
    level: null,
  },
  'user.role.change': {
    category: 'user',
    description: 'Менять роль пользователя',
    scope: 'global',
    level: null,
  },

  'audit.view': {
    category: 'audit',
    description: 'Смотреть весь audit log',
    scope: 'global',
    level: null,
  },
  'audit.view.scoped': {
    category: 'audit',
    description: 'Смотреть audit log своих аккаунтов',
    scope: 'account',
    level: 'READ_ONLY',
  },

  'browser.policy.view': {
    category: 'browser',
    description: 'Читать политики браузерного шелла',
    scope: 'global',
    level: null,
  },
  'browser.policy.manage': {
    category: 'browser',
    description: 'Изменять политики браузерного шелла',
    scope: 'global',
    level: null,
  },

  'section.manage': {
    category: 'section',
    description: 'Создавать разделы рабочего места и раздавать к ним доступ',
    scope: 'global',
    level: null,
  },

  'stays.view': {
    category: 'stays',
    description: 'Видеть модуль «Апартаменты» и общую базу квартир',
    scope: 'global',
    level: null,
  },
  'stays.book': {
    category: 'stays',
    description: 'Записывать брони, добавлять квартиры и комментарии',
    scope: 'global',
    level: null,
  },
  'stays.property.moderate': {
    category: 'stays',
    description: 'Отмечать квартиры проверенными и вносить в чёрный список',
    scope: 'global',
    level: null,
  },
  'stays.booking.view.all': {
    category: 'stays',
    description: 'Видеть все брони компании, а не только свои и по своим аккаунтам',
    scope: 'global',
    level: null,
  },
  'stays.account.manage': {
    category: 'stays',
    description: 'Подключать аккаунты Airbnb и Booking и выдавать к ним доступ',
    scope: 'global',
    level: null,
  },
};
